import React, { useState, useEffect } from 'react';
import { cn } from '@vaiu/utils';
import { roundMonetaryValue, formatMonetaryValue } from '@vaiu/utils/currency';
import { isMmMoneyCurrency } from '@vaiu/utils/mmMoney';
import type { ICurrencyService, CurrencyDetails } from '@vaiu/services';

interface CurrencyDisplayProps {
  amount: number;
  currency: string;
  /** When omitted, amounts render with {@link fallbackSymbol} and 2 decimal places only. */
  currencyService?: ICurrencyService;
  className?: string;
  showSign?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'success' | 'destructive' | 'muted';
  fallbackSymbol?: string; // Optional fallback symbol while loading
}

const CurrencyDisplay: React.FC<CurrencyDisplayProps> = ({
  amount,
  currency,
  currencyService,
  className,
  showSign = false,
  size = 'md',
  variant = 'default',
  fallbackSymbol = '$'
}) => {
  const [currencyDetails, setCurrencyDetails] = useState<CurrencyDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const meMoneyDisplayDecimals = isMmMoneyCurrency(currency) ? 0 : 2;

  useEffect(() => {
    let isMounted = true;

    const loadCurrencyDetails = async () => {
      const fetchDetails =
        currencyService &&
        typeof currencyService.getCurrencyDetails === 'function'
          ? currencyService.getCurrencyDetails.bind(currencyService)
          : null;

      if (!fetchDetails) {
        if (isMounted) {
          setCurrencyDetails({
            code: currency.toLowerCase(),
            symbol: fallbackSymbol,
            decimals: meMoneyDisplayDecimals,
          });
          setIsLoading(false);
        }
        return;
      }
      try {
        setIsLoading(true);
        const details = await fetchDetails(currency);
        if (isMounted) {
          setCurrencyDetails(details);
        }
      } catch (error) {
        console.warn(`Failed to load currency details for ${currency}:`, error);
        if (isMounted) {
          // Use fallback details
          setCurrencyDetails({
            code: currency.toLowerCase(),
            symbol: fallbackSymbol,
            decimals: meMoneyDisplayDecimals,
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadCurrencyDetails();

    return () => {
      isMounted = false;
    };
  }, [currency, fallbackSymbol, currencyService, meMoneyDisplayDecimals]);

  const formatCurrency = (value: number, details: CurrencyDetails) => {
    const absValue = Math.abs(value);
    // Round first to prevent floating point precision issues (e.g., 1.000003, 8.9999999)
    const roundedValue = roundMonetaryValue(absValue, details.decimals);
    const formattedAmount = formatMonetaryValue(roundedValue, details.decimals);
    return `${details.symbol}${formattedAmount}`;
  };

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg font-semibold'
  };

  const variantClasses = {
    default: 'text-foreground',
    success: 'text-green-600',
    destructive: 'text-red-600',
    muted: 'text-muted-foreground'
  };

  // Show loading or fallback while currency details are being fetched
  if (isLoading || !currencyDetails) {
    // Round first to prevent floating point precision issues
    const roundedAmount = roundMonetaryValue(Math.abs(amount), meMoneyDisplayDecimals);
    const fallbackAmount = formatMonetaryValue(roundedAmount, meMoneyDisplayDecimals);
    const sign = showSign ? (amount >= 0 ? '+' : '-') : (amount < 0 ? '-' : '');
    
    return (
      <span className={cn(
        'font-mono',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}>
        {sign}{fallbackSymbol}{fallbackAmount}
      </span>
    );
  }

  const sign = showSign ? (amount >= 0 ? '+' : '-') : (amount < 0 ? '-' : '');
  const formattedAmount = formatCurrency(amount, currencyDetails);

  return (
    <span className={cn(
      'font-mono',
      sizeClasses[size],
      variantClasses[variant],
      className
    )}>
      {sign}{formattedAmount}
    </span>
  );
};

export default CurrencyDisplay;
